import {
  IsArray,
  IsBoolean,
  IsIn,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  ValidateIf,
} from 'class-validator';
import { MEASUREMENT_UNITS, type MeasurementUnit } from '../../label/measurement-unit';
import { LABEL_SUGGESTION_FIELDS, type LabelSuggestionField } from './label-suggestion-field.enum';

/**
 * Not a Prisma enum: mirrors the XOR subject of `CounterLabelData`
 * (`recipeId` vs `ingredientId`) as a discriminant for this request, plus
 * `RECIPE_INGREDIENT_ROW` — the single recipe row the cook is editing, which
 * may not be persisted yet (a brand-new line in the form). Only `WEIGHT_G`
 * is meaningful for that subject: `FIELD_APPLICABLE_SUBJECTS` in
 * `label-suggestion.service.ts` is the enforcement point.
 */
export const LABEL_SUGGESTION_SUBJECT_TYPES = [
  'RECIPE',
  'INGREDIENT',
  'RECIPE_INGREDIENT_ROW',
] as const;
export type LabelSuggestionSubjectType = (typeof LABEL_SUGGESTION_SUBJECT_TYPES)[number];

const PERSISTED_SUBJECT_TYPES: LabelSuggestionSubjectType[] = ['RECIPE', 'INGREDIENT'];

/**
 * Deterministic suggestions always run for the requested `fields`; `useAi`
 * is the explicit opt-in the plan requires (decision 5: AI never runs on
 * open or on save, only when the user asks for it). `RECIPE_INGREDIENT_ROW`
 * is the exception: it is deterministic-only regardless of `useAi` — see the
 * comment on the AI branch in `label-suggestion.service.ts#getSuggestions`.
 */
export class RequestLabelSuggestionsDto {
  @IsIn(LABEL_SUGGESTION_SUBJECT_TYPES)
  subjectType!: LabelSuggestionSubjectType;

  /** Required for RECIPE/INGREDIENT — a persisted subject looked up by id. */
  @ValidateIf((dto: RequestLabelSuggestionsDto) =>
    PERSISTED_SUBJECT_TYPES.includes(dto.subjectType),
  )
  // Seed/demo data uses UUID-shaped identifiers whose version bits are not
  // RFC 4122-compliant (for example, 22000000-0000-0000-0000-000000000020).
  // The database column is a string, so validate the shape without requiring
  // a particular UUID version.
  @IsUUID('loose')
  subjectId?: string;

  /**
   * Inline description of the row for `RECIPE_INGREDIENT_ROW`, since the row
   * itself may not exist in the DB yet. `productName` doubles as the string
   * `parseProductFormat` reads a format from ("Passata 700ml").
   */
  @ValidateIf((dto: RequestLabelSuggestionsDto) => dto.subjectType === 'RECIPE_INGREDIENT_ROW')
  @IsString()
  productName?: string;

  @ValidateIf((dto: RequestLabelSuggestionsDto) => dto.subjectType === 'RECIPE_INGREDIENT_ROW')
  @IsNumber()
  quantity?: number;

  @ValidateIf((dto: RequestLabelSuggestionsDto) => dto.subjectType === 'RECIPE_INGREDIENT_ROW')
  @IsIn(MEASUREMENT_UNITS)
  unit?: MeasurementUnit;

  /**
   * The `Ingredient` (anagrafica) linked to this row, when the cook already
   * picked one — its `densityGPerMl` feeds the VOLUME branch of the weight
   * cascade. Optional: an unlinked row still resolves MASS/mass-format rows.
   */
  @IsOptional()
  @IsUUID('loose')
  ingredientId?: string;

  @IsArray()
  @IsIn(LABEL_SUGGESTION_FIELDS, { each: true })
  fields!: LabelSuggestionField[];

  @IsBoolean()
  useAi!: boolean;
}
